import { ICoin } from '../ff001-starstruck-lines/interfaces';
export type Board = (ICoin | null)[][];
export interface Config {
  reelsSetup: number[]; baseSpinsReels: string[][]; freeSpinsReels: string[][];
  wild: string; scat: string; countToTriggerFreeSpins: number; freeSpinsWinCount: number;
  freeSpinsWinCountByScatterCount: Record<number, number>;
  coinFeature: { coin: string; coinTrigger: number; coinTriggerSpinCount: number;
    coins: (string | number)[]; coinsOnTriggerWeights: number[]; coinsOnGameWeights: number[] };
}
export interface Entry { reelIndexes: number[]; forceCoinSpinsTrigger: boolean; plans: Board[] }
export interface Scenario {
  reelIndexes: number[]; forceCoinSpinsTrigger: boolean;
  otherData?: { coinSpins: Board[]; coinSpinIndex: number };
}
export const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
export const emptyBoard = (config: Config): Board => config.reelsSetup.map(rows => Array(rows).fill(null));
export const blankEntry = (config: Config): Entry => ({ reelIndexes: config.reelsSetup.map(() => 0), forceCoinSpinsTrigger: false, plans: [] });
export const cells = <T,>(board: T[][]) => board.flatMap((reel, r) => reel.map((value, row) => ({ reel: r, row, value })));
export const cellKey = (r: number, row: number) => `${r}:${row}`;
export function visibleWindow(entry: Entry, config: Config, free: boolean, sticky: Set<string> = new Set()) {
  const strips = free ? config.freeSpinsReels : config.baseSpinsReels;
  return strips.map((strip, r) => Array.from({ length: config.reelsSetup[r] }, (_, row) =>
    free && sticky.has(cellKey(r, row)) ? config.wild : strip[(entry.reelIndexes[r] + row) % strip.length]));
}
export function projectEntries(entries: Entry[], config: Config) {
  let remaining = 0; let sticky = new Set<string>();
  return entries.map(entry => {
    const free = remaining > 0;
    const held = new Set(sticky);
    const window = visibleWindow(entry, config, free, sticky);
    const scatters = cells(window).filter(c => c.value === config.scat).length;
    remaining = Math.max(0, remaining - 1) + (scatters >= config.countToTriggerFreeSpins ? config.freeSpinsWinCountByScatterCount[Math.min(scatters, 5)] : 0);
    sticky = free && remaining > 0 ? new Set(cells(window).filter(c => c.value === config.wild).map(c => cellKey(c.reel, c.row))) : new Set();
    return { free, held, window, remaining };
  });
}
export function coinOptions(config: Config, trigger: boolean) {
  const weights = trigger ? config.coinFeature.coinsOnTriggerWeights : config.coinFeature.coinsOnGameWeights;
  return config.coinFeature.coins.filter((_, i) => weights[i] > 0);
}
export function makeCoin(value: string | number, index: number): ICoin {
  const type = typeof value === 'number' ? 'price' : value as ICoin['coinType'];
  const multiplier = typeof value === 'number' ? value : 0;
  return { coinType: type, multiplier, totalWinInCredits: 0, enabled: true, startWindowIndex: index,
    history: [{ type, multiplier, totalWinInCredits: 0 }] };
}
export const coinValue = (coin: ICoin) => coin.coinType === 'price' ? coin.multiplier : coin.coinType;
export function scriptEntry(entry: Entry, config: Config, window: string[][]): Entry {
  const coinCells = cells(window).filter(c => c.value === config.coinFeature.coin);
  const forced = entry.forceCoinSpinsTrigger && coinCells.length > 0 && coinCells.length < config.coinFeature.coinTrigger;
  if (coinCells.length < config.coinFeature.coinTrigger && !forced) throw new Error('Select 6+ coins, or enable the 1–5 coin pot trigger.');
  const board = emptyBoard(config);
  const value = coinOptions(config, true)[0];
  coinCells.forEach(c => { board[c.reel][c.row] = makeCoin(value, 0); });
  if (forced) for (const c of cells(board)) {
    if (cells(board).filter(c => c.value).length >= config.coinFeature.coinTrigger) break;
    if (!c.value) board[c.reel][c.row] = makeCoin(value, 0);
  }
  return { ...entry, plans: [board] };
}
export function projectPlans(plans: Board[], config: Config) {
  const occupied = new Set<string>(); let remaining = config.coinFeature.coinTriggerSpinCount;
  return plans.map((board, index) => {
    const held = new Set(occupied);
    if (index > 0 && remaining === 0) throw new Error('Remove respins after the Coin Spins feature has ended.');
    let added = 0;
    for (const c of cells(board)) if (c.value) {
      const key = cellKey(c.reel, c.row);
      if (held.has(key)) throw new Error('Held coins cannot be replaced by a later respin.');
      occupied.add(key); added++;
    }
    if (index > 0) remaining = added ? config.coinFeature.coinTriggerSpinCount : remaining - 1;
    if (occupied.size === config.reelsSetup.reduce((a, b) => a + b, 0)) remaining = 0;
    return { held, occupied: new Set(occupied), remaining };
  });
}
export function serialize(entries: Entry[], config: Config): Scenario[] {
  const projections = projectEntries(entries, config);
  return entries.map((entry, index) => {
    const strips = projections[index].free ? config.freeSpinsReels : config.baseSpinsReels;
    if (entry.reelIndexes.length !== strips.length || entry.reelIndexes.some((n, r) => !Number.isInteger(n) || n < 0 || n >= strips[r].length))
      throw new Error(`Spin ${index + 1}: invalid reel stop.`);
    const result = { reelIndexes: [...entry.reelIndexes], forceCoinSpinsTrigger: entry.forceCoinSpinsTrigger };
    if (!entry.plans.length) {
      const coinCount = cells(projections[index].window).filter(c => c.value === config.coinFeature.coin).length;
      const triggers = coinCount >= config.coinFeature.coinTrigger || (coinCount > 0 && entry.forceCoinSpinsTrigger);
      const full = coinCount === config.reelsSetup.reduce((a, b) => a + b, 0);
      if (triggers && !full && index < entries.length - 1)
        throw new Error(`Spin ${index + 1}: enable Script Coin Spins before queueing the next spin, so the feature finishes first.`);
      return result;
    }
    const expected = scriptEntry(entry, config, projections[index].window).plans[0];
    const landed = cells(projections[index].window).filter(c => c.value === config.coinFeature.coin);
    if (cells(entry.plans[0]).filter(c => c.value).length !== cells(expected).filter(c => c.value).length ||
      landed.some(c => !entry.plans[0][c.reel]?.[c.row])) throw new Error(`Spin ${index + 1}: coin entry must preserve the reel coins and trigger count.`);
    if (!entry.forceCoinSpinsTrigger && cells(expected).some(c => !!c.value !== !!entry.plans[0][c.reel]?.[c.row]))
      throw new Error('Natural Coin Spins must use the landed coin positions.');
    entry.plans.forEach((board, step) => {
      if (board.length !== config.reelsSetup.length || board.some((reel, r) => !Array.isArray(reel) || reel.length !== config.reelsSetup[r])) throw new Error('Invalid Coin Spins grid.');
      for (const c of cells(board)) if (c.value && !coinOptions(config, step === 0).includes(coinValue(c.value))) throw new Error('Coin type is unavailable for this step.');
    });
    const projected = projectPlans(entry.plans, config);
    const plans = clone(entry.plans);
    // Finish with misses so subsequent queued Free Spins are not consumed as respins.
    for (let count = projected[projected.length - 1].remaining; count > 0; count--) plans.push(emptyBoard(config));
    return { ...result, otherData: { coinSpins: plans, coinSpinIndex: 0 } };
  });
}
export function deserialize(queue: any[], config: Config): Entry[] {
  if (!Array.isArray(queue) || !queue.length) throw new Error('Empty scenario preset.');
  const entries = queue.map(step => {
    if (!step || Object.keys(step).some(k => !['reelIndexes', 'forceCoinSpinsTrigger', 'otherData'].includes(k)) ||
      (step.forceCoinSpinsTrigger !== undefined && typeof step.forceCoinSpinsTrigger !== 'boolean') ||
      (step.otherData && (Object.keys(step.otherData).some(k => !['coinSpins', 'coinSpinIndex'].includes(k)) || step.otherData.coinSpinIndex !== 0)))
      throw new Error('This preset uses unsupported scenario fields; it has not been changed.');
    return { reelIndexes: clone(step.reelIndexes), forceCoinSpinsTrigger: step.forceCoinSpinsTrigger ?? false, plans: clone(step.otherData?.coinSpins ?? []) };
  });
  serialize(entries, config);
  return entries;
}
/** Bounded dynamic search over real strips, accounting for already sticky Wilds. */
export function findStops(config: Config, free: boolean, held: Set<string>, kind: 'pot' | 'coins' | 'free' | 'quiet') {
  const strips = free ? config.freeSpinsReels : config.baseSpinsReels;
  let paths = new Map<string, number[]>([['0:0', []]]);
  for (let r = 0; r < strips.length; r++) {
    const next = new Map<string, number[]>();
    for (const [counts, path] of Array.from(paths)) for (let stop = 0; stop < strips[r].length; stop++) {
      const [coins, scats] = counts.split(':').map(Number);
      const visible = Array.from({ length: config.reelsSetup[r] }, (_, row) => free && held.has(cellKey(r, row)) ? config.wild : strips[r][(stop + row) % strips[r].length]);
      const c = coins + visible.filter(s => s === config.coinFeature.coin).length;
      const s = scats + visible.filter(s => s === config.scat).length;
      const key = `${c}:${s}`;
      if (!next.has(key)) next.set(key, [...path, stop]);
    }
    paths = next;
  }
  for (const [counts, path] of Array.from(paths)) {
    const [c, s] = counts.split(':').map(Number);
    if (kind === 'free' ? s >= config.countToTriggerFreeSpins && c === 0
      : kind === 'quiet' ? c === 0 && s < config.countToTriggerFreeSpins
      : s < config.countToTriggerFreeSpins && (kind === 'pot' ? c > 0 && c < config.coinFeature.coinTrigger : c >= config.coinFeature.coinTrigger)) return path;
  }
  throw new Error('No matching combination is available on these reels with the current sticky Wilds.');
}
